// ─────────────────────────────────────────────────────────────────────────
// loginCore.js
//
// Shared login + post-login plumbing used by both login styles (the
// classic Login.js layout and the split "Welcome Back" LoginSplit
// layout). Keeping this in one place means the two skins can't drift
// apart on auth behaviour — they POST the same endpoint, surface the
// same license / branch errors, and run the same socket + localStorage
// hand-off after a successful login.
//
// The shared API:
//   • submitCredentials({ username, password }) → resolves with a tagged
//     object describing what happened. The caller maps each tag to its
//     own UI (alert vs. SwAl, full-page vs. inline error, etc.).
//   • finalizeLogin(user, { navigate, socket, setSuccess }) — runs the
//     same localStorage + socket registration + dashboard navigation
//     the classic Login has done since day one. Resume-window logic
//     for auto-logout sessions is preserved.
// ─────────────────────────────────────────────────────────────────────────

import axios from 'axios';
import { URL } from '../BaseURL';

// ── submitCredentials ────────────────────────────────────────────────────
// Returns one of:
//   { kind: 'ok',              user, licenseWarning, daysLeft }
//   { kind: 'first-login',     user }
//   { kind: 'branch-blocked',  message }
//   { kind: 'license-blocked', message }
//   { kind: 'error',           message }
//
// Never throws — every code path resolves with a tagged outcome so the
// UI code stays free of try/catch.
export async function submitCredentials({ username, password }) {
  try {
    const { data } = await axios.post(URL + 'login', { username, password });
    if (!data?.success) {
      return { kind: 'error', message: data?.message || 'Invalid username or password' };
    }
    const user = data.user;
    if (user?.first_login) {
      return { kind: 'first-login', user };
    }
    return {
      kind: 'ok',
      user,
      licenseWarning: !!data.licenseWarning,
      daysLeft: data.daysLeft || null,
    };
  } catch (err) {
    const data   = err.response?.data;
    const status = err.response?.status;
    if (data?.branchBlocked) {
      return {
        kind: 'branch-blocked',
        message: data?.message || 'Your branch is not currently active.',
      };
    }
    if (status === 403 || data?.licenseBlocked) {
      return {
        kind: 'license-blocked',
        message: data?.message || 'Your license has expired or been suspended.',
      };
    }
    return {
      kind: 'error',
      message: data?.message || 'Invalid username or password',
    };
  }
}

// ── finalizeLogin ────────────────────────────────────────────────────────
// Mirrors the long-standing post-login flow from Login.js: persist user
// info, register with the socket for live license events, and navigate
// to the dashboard (resuming the prior panel if the same user was auto-
// logged-out under an hour ago).
export function finalizeLogin(user, { navigate, socket, setSuccess }) {
  if (typeof setSuccess === 'function') setSuccess(true);

  const userInfo = {
    name:       user.name,
    email:      user.email,
    department: user.department,
    role:       user.role,
    staff_id:   user.staff_id,
    is_staff:   user.is_staff,
    trans_code: user.id,
    username:   user.username,
    created_at: user.created_at,
    branch:     user.branch || null,
  };
  localStorage.setItem('userInfo', JSON.stringify(userInfo));

  // Default license states until the socket pushes real values.
  localStorage.setItem('license_ai',        JSON.stringify({ allowed: true }));
  localStorage.setItem('license_call_chat', JSON.stringify({ allowed: true }));

  try {
    if (!socket.connected) socket.connect();
    const registerUser = () => socket.emit('register-user', { userId: String(user.id) });
    if (socket.connected) registerUser();
    else socket.once('connect', registerUser);

    socket.off('ai-license');
    socket.off('call-chat-license');
    socket.on('ai-license', (data) => {
      localStorage.setItem('license_ai', JSON.stringify(data));
    });
    socket.on('call-chat-license', (data) => {
      localStorage.setItem('license_call_chat', JSON.stringify(data));
    });
  } catch (err) {
    console.error('[loginCore] socket registration error:', err);
  }

  // Resume the prior dashboard panel if the auto-logout happened recently
  // for the same user on the same calendar day (under an hour ago). Any
  // missing field falls back to the default /dashboard landing.
  const RESUME_WINDOW_MS = 60 * 60 * 1000;
  let resumeRoute = '/dashboard';
  try {
    const raw = localStorage.getItem('lastSession');
    if (raw) {
      const last = JSON.parse(raw);
      const sameUser    = last?.username === user.username;
      const sameDay     = last?.logoutDate === new Date().toDateString();
      const withinWindow = Date.now() - (last?.logoutTime || 0) < RESUME_WINDOW_MS;
      const validRoute  = typeof last?.route === 'string'
        && last.route.startsWith('/dashboard/')
        && last.route !== '/dashboard/';
      if (sameUser && sameDay && withinWindow && validRoute) {
        resumeRoute = last.route;
      }
    }
  } catch (_) { /* fall back to default */ }
  localStorage.removeItem('lastSession');

  setTimeout(() => navigate(resumeRoute), 1000);
}
